import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiUpdateMyProfile } from "@/lib/api";
import { CANADIAN_PROVINCES } from "@/constants/provinces";

const EDITABLE = [
  "contact_name", "business_name", "company_name",
  "phone", "website", "business_address", "province",
  "brands_stocked", "store_classifications",
];

function pickEditable(user) {
  const out = {};
  EDITABLE.forEach((k) => { out[k] = user?.[k] || ""; });
  return out;
}

export default function Account() {
  const { user, loading, refresh } = useAuth();
  const [form, setForm] = useState(() => pickEditable(user));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm(pickEditable(user));
  }, [user]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-foreground/70" data-testid="account-loading">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiUpdateMyProfile(form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-card" data-testid="account-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Your account</p>
        <h1
          className="font-serif text-3xl md:text-4xl mb-3 text-primary"
          style={{ fontFamily: '"Snowyn Snowman", "Caveat Brush", cursive' }}
        >
          Account details
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Update your business info. Email changes and password resets are handled separately —
          contact us if you need help.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <ReadOnlyRow label="Email" value={user.email} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Your name">
              <input
                data-testid="account-contact-name"
                value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Store name">
              <input
                data-testid="account-business-name"
                value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Company / Business name (legal)" hint="For invoices">
            <input
              data-testid="account-company-name"
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone">
              <input
                data-testid="account-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Website">
              <input
                data-testid="account-website"
                type="text"
                inputMode="url"
                placeholder="your-store.com"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Business address">
            <input
              data-testid="account-business-address"
              value={form.business_address}
              onChange={(e) => set("business_address", e.target.value)}
              placeholder="Street, city, postal code"
              className={inputCls}
            />
          </Field>

          <Field label="Province" hint="Used for tax">
            <select
              data-testid="account-province"
              value={form.province}
              onChange={(e) => set("province", e.target.value)}
              className={inputCls}
            >
              <option value="">Select province</option>
              {CANADIAN_PROVINCES.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Other brands you stock">
            <textarea
              data-testid="account-brands-stocked"
              rows={2}
              value={form.brands_stocked}
              onChange={(e) => set("brands_stocked", e.target.value)}
              className={`${inputCls} resize-y`}
            />
          </Field>

          <Field label="Store classifications">
            <input
              data-testid="account-store-classifications"
              placeholder="Women's, Men's, Contemporary, etc."
              value={form.store_classifications}
              onChange={(e) => set("store_classifications", e.target.value)}
              className={inputCls}
            />
          </Field>

          <button
            data-testid="account-save"
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto px-6 py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary bg-background";

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-1.5 gap-0.5 sm:gap-3">
        <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</label>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function ReadOnlyRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded bg-secondary border border-border">
      <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}
